import { useEffect, useState } from "react";
import {
  createAiOrder,
  fetchAiCapabilities,
  fetchAiOrder,
  type AiCapabilities
} from "../lib/api";
import type {
  DragonDossier,
  EncounterPhase,
  Faction,
  Facet,
  HistoryEvent,
  HoardItem,
  LairRoom,
  RuleAction,
  ValidationIssue
} from "../types";
import { EditableText } from "./Editable";
import { FactionGraph } from "./FactionGraph";
import { Icon } from "./Icon";
import { Overview } from "./Overview";

export const TABS = [
  ["overview", "Overview"],
  ["history", "History"],
  ["lair", "Lair"],
  ["hoard", "Hoard"],
  ["factions", "Factions"],
  ["encounters", "Encounters"],
  ["hooks", "Hooks"],
  ["rules", "Rules"],
  ["export", "Export"]
] as const;

interface DetailsProps {
  activeTab: string;
  dossier: DragonDossier;
  issues: ValidationIssue[];
  selectedFactionId: string | null;
  onSelectFaction: (id: string) => void;
  onSelectRoom: (id: string) => void;
  onEdit: (mutator: (draft: DragonDossier) => void, message?: string) => void;
  onReroll: (facet: Facet) => void;
  onExportJson: () => void;
  onExportMarkdown: () => void;
  onExportSvg: () => void;
  onExportPng: () => void;
  onPrint: () => void;
}

function moveItem<T>(items: T[], from: number, to: number): void {
  if (to < 0 || to >= items.length) return;
  const [item] = items.splice(from, 1);
  items.splice(to, 0, item);
}

function HistoryEditor({ dossier, onEdit }: Pick<DetailsProps, "dossier" | "onEdit">) {
  return (
    <div className="detail-canvas">
      <div className="detail-toolbar">
        <div>
          <span className="eyebrow">Chronicle editor</span>
          <h2>{dossier.dragon.name}'s history</h2>
        </div>
        <button
          type="button"
          className="secondary-button secondary-button--compact"
          onClick={() =>
            onEdit((draft) => {
              draft.history.push({
                id: `event-custom-${crypto.randomUUID()}`,
                age: draft.dragon.ageYears,
                era: "Now",
                title: "New history event",
                description: "Describe the cause, consequence and surviving witness.",
                consequence: "Describe what changes because this event happened.",
                witness: draft.dragon.name,
                linkedEntity: draft.dragon.name,
                linkedEntities: [
                  {
                    id: draft.dragon.name,
                    type: "dragon",
                    name: draft.dragon.name,
                    reason: "custom event"
                  }
                ]
              });
            }, "History event added")
          }
        >
          + Add event
        </button>
      </div>
      <div className="editor-list editor-list--timeline">
        {dossier.history.map((event, index) => (
          <article className="editor-card timeline-editor-card" key={event.id}>
            <div className="editor-card__handle">
              <Icon name="spark" />
            </div>
            <label>
              <span>Age</span>
              <input
                type="number"
                value={event.age}
                onChange={(change) =>
                  onEdit((draft) => {
                    draft.history[index].age = Number(change.target.value);
                  })
                }
              />
            </label>
            <div className="editor-card__body">
              <EditableText
                label={`History title ${index + 1}`}
                className="editor-card__title"
                value={event.title}
                onChange={(value) =>
                  onEdit((draft) => {
                    draft.history[index].title = value;
                  })
                }
              />
              <div className="history-meta-row">
                <EditableText
                  label={`History era ${index + 1}`}
                  value={event.era ?? ""}
                  onChange={(value) =>
                    onEdit((draft) => {
                      draft.history[index].era = value;
                    })
                  }
                />
                <EditableText
                  label={`History witness ${index + 1}`}
                  value={event.witness ?? ""}
                  onChange={(value) =>
                    onEdit((draft) => {
                      draft.history[index].witness = value;
                    })
                  }
                />
              </div>
              <EditableText
                label={`History description ${index + 1}`}
                multiline
                value={event.description}
                onChange={(value) =>
                  onEdit((draft) => {
                    draft.history[index].description = value;
                  })
                }
              />
              <EditableText
                label={`History consequence ${index + 1}`}
                multiline
                value={event.consequence ?? ""}
                onChange={(value) =>
                  onEdit((draft) => {
                    draft.history[index].consequence = value;
                  })
                }
              />
              <EditableText
                label={`Linked entity ${index + 1}`}
                value={event.linkedEntity}
                onChange={(value) =>
                  onEdit((draft) => {
                    draft.history[index].linkedEntity = value;
                  })
                }
              />
              {event.linkedEntities?.length ? (
                <div className="history-link-chips" aria-label={`History links ${index + 1}`}>
                  {event.linkedEntities.map((link) => (
                    <span key={`${event.id}-${link.type}-${link.id}`}>
                      {link.type}: {link.name}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="delete-button"
              aria-label={`Delete ${event.title}`}
              onClick={() =>
                onEdit((draft) => {
                  draft.history.splice(index, 1);
                }, "History event deleted")
              }
            >
              ×
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}

function LairEditor({
  dossier,
  onEdit,
  onSelectRoom
}: Pick<DetailsProps, "dossier" | "onEdit" | "onSelectRoom">) {
  return (
    <div className="detail-canvas">
      <div className="detail-toolbar">
        <div>
          <span className="eyebrow">Map structure</span>
          <EditableText
            label="Lair name"
            className="detail-title-input"
            value={dossier.lair.name}
            onChange={(value) => onEdit((draft) => void (draft.lair.name = value))}
          />
        </div>
        <button
          type="button"
          className="secondary-button secondary-button--compact"
          onClick={() =>
            onEdit((draft) => {
              const room: LairRoom = {
                id: `room-custom-${crypto.randomUUID()}`,
                name: "New chamber",
                role: "anomaly",
                danger: "moderate",
                occupants: "No permanent occupants",
                hazards: [],
                treasure: "No obvious treasure",
                tacticalNote: "Define how this room changes an encounter.",
                x: 50,
                y: 50,
                radius: 7
              };
              const previous = draft.lair.rooms.at(-1);
              draft.lair.rooms.push(room);
              if (previous) {
                draft.lair.connections.push({
                  id: `path-custom-${crypto.randomUUID()}`,
                  from: previous.id,
                  to: room.id,
                  secret: false,
                  escape: false
                });
              }
            }, "Room added")
          }
        >
          + Add room
        </button>
      </div>
      <EditableText
        label="Lair summary"
        multiline
        rows={2}
        value={dossier.lair.summary}
        onChange={(value) => onEdit((draft) => void (draft.lair.summary = value))}
      />
      <div className="lair-layout-panel">
        <label>
          <span>Layout</span>
          <EditableText
            label="Lair layout"
            value={dossier.lair.layout ?? dossier.lair.type}
            onChange={(value) => onEdit((draft) => void (draft.lair.layout = value))}
          />
        </label>
        <div className="map-note-grid">
          {(dossier.lair.mapNotes ?? []).map((note, index) => (
            <EditableText
              key={`${dossier.lair.name}-note-${index}`}
              label={`Map note ${index + 1}`}
              value={note}
              onChange={(value) =>
                onEdit((draft) => {
                  if (!draft.lair.mapNotes) draft.lair.mapNotes = [];
                  draft.lair.mapNotes[index] = value;
                })
              }
            />
          ))}
        </div>
      </div>
      <div className="room-grid">
        {dossier.lair.rooms.map((room, index) => (
          <article className="room-card" key={room.id}>
            <button type="button" className="room-card__open" onClick={() => onSelectRoom(room.id)}>
              <span className="room-card__number">{index + 1}</span>
              <span>
                <strong>{room.name}</strong>
                <small>
                  {room.role} · {room.danger}
                </small>
              </span>
            </button>
            <button
              type="button"
              className="delete-button"
              aria-label={`Delete ${room.name}`}
              disabled={dossier.lair.rooms.length <= 3}
              onClick={() =>
                onEdit((draft) => {
                  draft.lair.rooms = draft.lair.rooms.filter((candidate) => candidate.id !== room.id);
                  draft.lair.connections = draft.lair.connections.filter(
                    (connection) => connection.from !== room.id && connection.to !== room.id
                  );
                }, "Room deleted")
              }
            >
              ×
            </button>
          </article>
        ))}
      </div>
      <div className="detail-toolbar detail-toolbar--minor">
        <h3>Regional effects</h3>
        <button
          type="button"
          className="plain-button"
          onClick={() =>
            onEdit((draft) => {
              draft.regionalEffects.push({
                id: `regional-custom-${crypto.randomUUID()}`,
                name: "New regional effect",
                description: "Describe how the dragon changes the surrounding region."
              });
            })
          }
        >
          + Add effect
        </button>
      </div>
      <div className="editor-list editor-list--compact">
        {dossier.regionalEffects.map((effect, index) => (
          <article className="editor-card" key={effect.id}>
            <div className="editor-card__body">
              <EditableText
                label={`Regional effect ${index + 1}`}
                className="editor-card__title"
                value={effect.name}
                onChange={(value) =>
                  onEdit((draft) => {
                    draft.regionalEffects[index].name = value;
                  })
                }
              />
              <EditableText
                label={`Regional effect description ${index + 1}`}
                multiline
                value={effect.description}
                onChange={(value) =>
                  onEdit((draft) => {
                    draft.regionalEffects[index].description = value;
                  })
                }
              />
            </div>
            <button
              type="button"
              className="delete-button"
              onClick={() => onEdit((draft) => void draft.regionalEffects.splice(index, 1))}
            >
              ×
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}

function HoardEditor({ dossier, onEdit }: Pick<DetailsProps, "dossier" | "onEdit">) {
  const distribution = dossier.hoard.distribution;
  return (
    <div className="detail-canvas">
      <div className="detail-toolbar">
        <div>
          <span className="eyebrow">Vault ledger</span>
          <h2>{dossier.hoard.totalValue.toLocaleString("en-US")} gp</h2>
        </div>
        <label className="inline-number">
          Total value
          <input
            type="number"
            min="0"
            step="100"
            value={dossier.hoard.totalValue}
            onChange={(event) =>
              onEdit((draft) => {
                draft.hoard.totalValue = Number(event.target.value);
              })
            }
          />
        </label>
      </div>
      <div className="distribution-editor">
        {(Object.keys(distribution) as Array<keyof typeof distribution>).map((key) => (
          <label key={key}>
            <span>{key}</span>
            <input
              type="range"
              min="0"
              max="100"
              value={distribution[key]}
              onChange={(event) =>
                onEdit((draft) => {
                  draft.hoard.distribution[key] = Number(event.target.value);
                })
              }
            />
            <strong>{distribution[key]}%</strong>
          </label>
        ))}
      </div>
      <div className="detail-toolbar detail-toolbar--minor">
        <h3>Signature treasures</h3>
        <button
          type="button"
          className="secondary-button secondary-button--compact"
          onClick={() =>
            onEdit((draft) => {
              const item: HoardItem = {
                id: `item-custom-${crypto.randomUUID()}`,
                name: "Unnamed signature treasure",
                rarity: "Signature relic",
                value: 1000,
                claimedBy: "An unknown claimant",
                origin: "Its provenance has not been established.",
                complication: "Removing it will reveal the complication.",
                hook: "Someone is willing to risk a great deal to acquire it."
              };
              draft.hoard.items.push(item);
            }, "Treasure added")
          }
        >
          + Add treasure
        </button>
      </div>
      <div className="treasure-grid">
        {dossier.hoard.items.map((item, index) => (
          <article className="treasure-card" key={item.id}>
            <div className="treasure-card__sigil">
              <Icon name="gem" />
            </div>
            <EditableText
              label={`Treasure name ${index + 1}`}
              className="editor-card__title"
              value={item.name}
              onChange={(value) => onEdit((draft) => void (draft.hoard.items[index].name = value))}
            />
            <div className="treasure-card__meta">
              <EditableText
                label={`Treasure rarity ${index + 1}`}
                value={item.rarity}
                onChange={(value) => onEdit((draft) => void (draft.hoard.items[index].rarity = value))}
              />
              <input
                aria-label={`Treasure value ${index + 1}`}
                type="number"
                value={item.value}
                onChange={(event) =>
                  onEdit((draft) => {
                    draft.hoard.items[index].value = Number(event.target.value);
                  })
                }
              />
            </div>
            <label>
              <span>Claimed by</span>
              <EditableText
                label={`Treasure claimant ${index + 1}`}
                value={item.claimedBy}
                onChange={(value) => onEdit((draft) => void (draft.hoard.items[index].claimedBy = value))}
              />
            </label>
            <label>
              <span>Origin</span>
              <EditableText
                label={`Treasure origin ${index + 1}`}
                multiline
                value={item.origin}
                onChange={(value) => onEdit((draft) => void (draft.hoard.items[index].origin = value))}
              />
            </label>
            <label>
              <span>Complication</span>
              <EditableText
                label={`Treasure complication ${index + 1}`}
                multiline
                value={item.complication}
                onChange={(value) =>
                  onEdit((draft) => void (draft.hoard.items[index].complication = value))
                }
              />
            </label>
            <label>
              <span>Hook</span>
              <EditableText
                label={`Treasure hook ${index + 1}`}
                multiline
                value={item.hook}
                onChange={(value) => onEdit((draft) => void (draft.hoard.items[index].hook = value))}
              />
            </label>
            <button
              type="button"
              className="delete-button treasure-card__delete"
              onClick={() => onEdit((draft) => void draft.hoard.items.splice(index, 1), "Treasure deleted")}
            >
              ×
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}

function FactionsEditor({
  dossier,
  selectedFactionId,
  onSelectFaction,
  onEdit
}: Pick<DetailsProps, "dossier" | "selectedFactionId" | "onSelectFaction" | "onEdit">) {
  return (
    <div className="detail-canvas detail-canvas--graph">
      <div className="detail-toolbar">
        <div>
          <span className="eyebrow">Influence network</span>
          <h2>Factions around {dossier.dragon.name}</h2>
        </div>
        <button
          type="button"
          className="secondary-button secondary-button--compact"
          onClick={() =>
            onEdit((draft) => {
              const faction: Faction = {
                id: `faction-custom-${crypto.randomUUID()}`,
                name: "New faction",
                kind: "independent interest",
                relation: "bargains",
                power: 4,
                goal: "Define what this faction wants from the dragon.",
                color: "#b58b4e",
                x: 84,
                y: 50
              };
              draft.factions.push(faction);
              draft.relationships.push({
                id: `relationship-custom-${crypto.randomUUID()}`,
                source: faction.id,
                target: "dragon",
                type: faction.relation,
                label: faction.relation
              });
            }, "Faction added")
          }
        >
          + Add faction
        </button>
      </div>
      <div className="large-graph">
        <FactionGraph
          dragonName={dossier.dragon.name}
          factions={dossier.factions}
          relationships={dossier.relationships}
          accent={dossier.appearance.accent}
          selectedId={selectedFactionId}
          onSelect={onSelectFaction}
          onMove={(id, x, y) =>
            onEdit((draft) => {
              const faction = draft.factions.find((candidate) => candidate.id === id);
              if (faction) {
                faction.x = x;
                faction.y = y;
              }
            })
          }
        />
      </div>
      <div className="faction-list">
        {dossier.factions.map((faction, index) => (
          <div
            role="button"
            tabIndex={0}
            className={`faction-list__item ${selectedFactionId === faction.id ? "is-selected" : ""}`}
            key={faction.id}
            onClick={() => onSelectFaction(faction.id)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") onSelectFaction(faction.id);
            }}
          >
            <span style={{ background: faction.color }} />
            <strong>{faction.name}</strong>
            <small>
              {faction.kind} · {faction.relation}
            </small>
            <button
              type="button"
              className="delete-button"
              aria-label={`Delete ${faction.name}`}
              onClick={(event) => {
                event.stopPropagation();
                onEdit((draft) => {
                  const removed = draft.factions[index].id;
                  draft.factions.splice(index, 1);
                  draft.relationships = draft.relationships.filter(
                    (relationship) =>
                      relationship.source !== removed && relationship.target !== removed
                  );
                }, "Faction deleted");
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EncounterEditor({ dossier, onEdit }: Pick<DetailsProps, "dossier" | "onEdit">) {
  return (
    <div className="detail-canvas">
      <div className="detail-toolbar">
        <div>
          <span className="eyebrow">DM procedure</span>
          <h2>Encounter script</h2>
        </div>
        <button
          type="button"
          className="secondary-button secondary-button--compact"
          onClick={() =>
            onEdit((draft) => {
              const phase: EncounterPhase = {
                id: `phase-custom-${crypto.randomUUID()}`,
                kind: "combat",
                title: "New encounter phase",
                steps: ["Describe what changes and what the dragon wants."]
              };
              draft.encounterScript.push(phase);
            }, "Encounter phase added")
          }
        >
          + Add phase
        </button>
      </div>
      <div className="editor-list">
        {dossier.encounterScript.map((phase, index) => (
          <article className={`editor-card phase-editor phase-editor--${phase.kind}`} key={phase.id}>
            <div className="phase-editor__order">
              <button
                type="button"
                aria-label={`Move ${phase.title} up`}
                disabled={index === 0}
                onClick={() =>
                  onEdit((draft) => moveItem(draft.encounterScript, index, index - 1), "Phase moved")
                }
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={`Move ${phase.title} down`}
                disabled={index === dossier.encounterScript.length - 1}
                onClick={() =>
                  onEdit((draft) => moveItem(draft.encounterScript, index, index + 1), "Phase moved")
                }
              >
                ↓
              </button>
            </div>
            <select
              aria-label={`Phase type ${index + 1}`}
              value={phase.kind}
              onChange={(event) =>
                onEdit((draft) => {
                  draft.encounterScript[index].kind = event.target.value as EncounterPhase["kind"];
                })
              }
            >
              <option>social</option>
              <option>stealth</option>
              <option>combat</option>
              <option>parley</option>
              <option>escape</option>
              <option>treasure</option>
            </select>
            <div className="editor-card__body">
              <EditableText
                label={`Phase title ${index + 1}`}
                className="editor-card__title"
                value={phase.title}
                onChange={(value) =>
                  onEdit((draft) => {
                    draft.encounterScript[index].title = value;
                  })
                }
              />
              {phase.steps.map((step, stepIndex) => (
                <div className="step-editor" key={`${phase.id}-${stepIndex}`}>
                  <span>{stepIndex + 1}</span>
                  <EditableText
                    label={`Phase ${index + 1} step ${stepIndex + 1}`}
                    value={step}
                    onChange={(value) =>
                      onEdit((draft) => {
                        draft.encounterScript[index].steps[stepIndex] = value;
                      })
                    }
                  />
                  <button
                    type="button"
                    className="delete-button"
                    onClick={() =>
                      onEdit((draft) => void draft.encounterScript[index].steps.splice(stepIndex, 1))
                    }
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="plain-button"
                onClick={() =>
                  onEdit((draft) => {
                    draft.encounterScript[index].steps.push("New encounter instruction.");
                  })
                }
              >
                + Add step
              </button>
            </div>
            <button
              type="button"
              className="delete-button"
              onClick={() =>
                onEdit((draft) => void draft.encounterScript.splice(index, 1), "Phase deleted")
              }
            >
              ×
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}

function HooksEditor({ dossier, onEdit }: Pick<DetailsProps, "dossier" | "onEdit">) {
  return (
    <div className="detail-canvas">
      <div className="detail-toolbar">
        <div>
          <span className="eyebrow">Adventure starters</span>
          <h2>Hooks with consequences</h2>
        </div>
        <button
          type="button"
          className="secondary-button secondary-button--compact"
          onClick={() =>
            onEdit((draft) => {
              draft.hooks.push({
                id: `hook-custom-${crypto.randomUUID()}`,
                title: "New adventure hook",
                premise: "Describe why the party becomes involved.",
                stakes: "Describe what changes if they fail.",
                linkedEntity: draft.dragon.name
              });
            }, "Hook added")
          }
        >
          + Add hook
        </button>
      </div>
      <div className="hook-grid">
        {dossier.hooks.map((hook, index) => (
          <article className="hook-card" key={hook.id}>
            <span className="hook-card__index">{String(index + 1).padStart(2, "0")}</span>
            <EditableText
              label={`Hook title ${index + 1}`}
              className="editor-card__title"
              value={hook.title}
              onChange={(value) => onEdit((draft) => void (draft.hooks[index].title = value))}
            />
            <EditableText
              label={`Hook premise ${index + 1}`}
              multiline
              value={hook.premise}
              onChange={(value) => onEdit((draft) => void (draft.hooks[index].premise = value))}
            />
            <label>
              <span>Stakes</span>
              <EditableText
                label={`Hook stakes ${index + 1}`}
                multiline
                value={hook.stakes}
                onChange={(value) => onEdit((draft) => void (draft.hooks[index].stakes = value))}
              />
            </label>
            <label>
              <span>Linked entity</span>
              <EditableText
                label={`Hook link ${index + 1}`}
                value={hook.linkedEntity}
                onChange={(value) =>
                  onEdit((draft) => void (draft.hooks[index].linkedEntity = value))
                }
              />
            </label>
            <button
              type="button"
              className="delete-button hook-card__delete"
              onClick={() => onEdit((draft) => void draft.hooks.splice(index, 1), "Hook deleted")}
            >
              ×
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}

function RulesEditor({ dossier, onEdit }: Pick<DetailsProps, "dossier" | "onEdit">) {
  const abilityEntries = Object.entries(dossier.rules.abilities) as Array<
    [keyof typeof dossier.rules.abilities, number]
  >;
  return (
    <div className="detail-canvas">
      <div className="rules-banner">
        <strong>{dossier.rules.adapter.toUpperCase()}</strong>
        <span>{dossier.rules.notice}</span>
      </div>
      <div className="rule-summary-grid">
        <label>
          Armor class
          <input
            type="number"
            value={dossier.rules.armorClass}
            onChange={(event) =>
              onEdit((draft) => void (draft.rules.armorClass = Number(event.target.value)))
            }
          />
        </label>
        <label>
          Hit points
          <input
            type="number"
            value={dossier.rules.hitPoints}
            onChange={(event) =>
              onEdit((draft) => void (draft.rules.hitPoints = Number(event.target.value)))
            }
          />
        </label>
        <label>
          Hit dice
          <EditableText
            label="Hit dice"
            value={dossier.rules.hitDice}
            onChange={(value) => onEdit((draft) => void (draft.rules.hitDice = value))}
          />
        </label>
        <label>
          Proficiency
          <input
            type="number"
            value={dossier.rules.proficiencyBonus}
            onChange={(event) =>
              onEdit((draft) => void (draft.rules.proficiencyBonus = Number(event.target.value)))
            }
          />
        </label>
      </div>
      <div className="ability-grid">
        {abilityEntries.map(([ability, score]) => (
          <label key={ability}>
            <span>{ability}</span>
            <input
              type="number"
              value={score}
              onChange={(event) =>
                onEdit((draft) => {
                  draft.rules.abilities[ability] = Number(event.target.value);
                })
              }
            />
          </label>
        ))}
      </div>
      <div className="detail-toolbar detail-toolbar--minor">
        <h3>Traits and actions</h3>
        <button
          type="button"
          className="secondary-button secondary-button--compact"
          onClick={() =>
            onEdit((draft) => {
              const action: RuleAction = {
                id: `action-custom-${crypto.randomUUID()}`,
                name: "New action",
                kind: "action",
                description: "Describe the action and its intended tactical role.",
                homebrew: true
              };
              draft.rules.actions.push(action);
            }, "Rule action added")
          }
        >
          + Add action
        </button>
      </div>
      <div className="editor-list">
        {dossier.rules.actions.map((action, index) => (
          <article className="editor-card rule-action-card" key={action.id}>
            <select
              value={action.kind}
              onChange={(event) =>
                onEdit((draft) => {
                  draft.rules.actions[index].kind = event.target.value as RuleAction["kind"];
                })
              }
            >
              <option>trait</option>
              <option>action</option>
              <option>legendary</option>
              <option>lair</option>
            </select>
            <div className="editor-card__body">
              <EditableText
                label={`Action name ${index + 1}`}
                className="editor-card__title"
                value={action.name}
                onChange={(value) =>
                  onEdit((draft) => void (draft.rules.actions[index].name = value))
                }
              />
              <EditableText
                label={`Action description ${index + 1}`}
                multiline
                value={action.description}
                onChange={(value) =>
                  onEdit((draft) => void (draft.rules.actions[index].description = value))
                }
              />
            </div>
            <label className="homebrew-toggle">
              <input
                type="checkbox"
                checked={action.homebrew}
                onChange={(event) =>
                  onEdit((draft) => {
                    draft.rules.actions[index].homebrew = event.target.checked;
                  })
                }
              />
              Homebrew
            </label>
            <button
              type="button"
              className="delete-button"
              onClick={() => onEdit((draft) => void draft.rules.actions.splice(index, 1))}
            >
              ×
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}

function ExportView({
  dossier,
  issues,
  onEdit,
  onExportJson,
  onExportMarkdown,
  onExportSvg,
  onExportPng,
  onPrint
}: Pick<
  DetailsProps,
  | "dossier"
  | "issues"
  | "onEdit"
  | "onExportJson"
  | "onExportMarkdown"
  | "onExportSvg"
  | "onExportPng"
  | "onPrint"
>) {
  const [capabilities, setCapabilities] = useState<AiCapabilities | null>(null);
  const [style, setStyle] = useState("codex");
  const [quality, setQuality] = useState("medium");
  const [aiStatus, setAiStatus] = useState("Checking premium portrait availability…");
  const [ordering, setOrdering] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchAiCapabilities()
      .then((value) => {
        if (!cancelled) {
          setCapabilities(value);
          setAiStatus(
            value.enabled
              ? "Premium portrait generation is available."
              : value.reason ?? "Unavailable."
          );
        }
      })
      .catch(() => {
        if (!cancelled) setAiStatus("Premium portrait service is not configured.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const orderId = new URLSearchParams(window.location.search).get("ai_order");
    if (!orderId) return;
    let cancelled = false;
    let timer = 0;
    const poll = async () => {
      try {
        const order = await fetchAiOrder(orderId);
        if (cancelled) return;
        setAiStatus(`Portrait order: ${order.status}`);
        if (order.status === "completed" && order.assetUrl) {
          onEdit((draft) => {
            draft.appearance.portrait = order.assetUrl!;
            draft.assets.portrait = order.assetUrl!;
          }, "Premium portrait applied");
          const url = new URL(window.location.href);
          url.searchParams.delete("ai_order");
          window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
          return;
        }
        if (!["failed", "refunded"].includes(order.status)) {
          timer = window.setTimeout(poll, 2500);
        }
      } catch (error) {
        if (!cancelled) {
          setAiStatus(
            error instanceof Error ? error.message : "Could not retrieve portrait order."
          );
        }
      }
    };
    void poll();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [onEdit]);

  const startOrder = async () => {
    if (!dossier.id) {
      setAiStatus("Save this dossier before ordering a premium portrait.");
      return;
    }
    setOrdering(true);
    try {
      const order = await createAiOrder(dossier.id, style, quality);
      window.location.assign(order.checkoutUrl);
    } catch (error) {
      setAiStatus(error instanceof Error ? error.message : "Checkout could not be created.");
      setOrdering(false);
    }
  };

  return (
    <div className="detail-canvas export-view">
      <div>
        <span className="eyebrow">Portable dossier</span>
        <h2>Export {dossier.dragon.name}</h2>
        <p>Every export includes the seed, generator version and rules adapter.</p>
      </div>
      <div className="export-grid">
        <button type="button" onClick={onExportJson}>
          <strong>JSON</strong>
          <span>Canonical machine-readable dossier</span>
        </button>
        <button type="button" onClick={onExportMarkdown}>
          <strong>Markdown</strong>
          <span>Editable campaign notes</span>
        </button>
        <button type="button" onClick={onExportSvg}>
          <strong>SVG map</strong>
          <span>Scalable lair map</span>
        </button>
        <button type="button" onClick={onExportPng}>
          <strong>PNG map</strong>
          <span>Parchment image at 1600×1000</span>
        </button>
        <button type="button" onClick={onPrint}>
          <strong>Print / PDF</strong>
          <span>Clean, ink-conscious dossier</span>
        </button>
        <div className="premium-card">
          <strong>Premium portrait</strong>
          <span>{aiStatus}</span>
          <div className="premium-card__options">
            <select value={style} onChange={(event) => setStyle(event.target.value)}>
              {(capabilities?.styles ?? ["codex", "painterly", "ink"]).map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
            <select value={quality} onChange={(event) => setQuality(event.target.value)}>
              {(capabilities?.qualities ?? ["low", "medium", "high"]).map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={!capabilities?.enabled || ordering}
            onClick={() => void startOrder()}
          >
            {ordering
              ? "Opening checkout…"
              : capabilities
                ? `Order · ${(capabilities.priceCents / 100).toLocaleString("en", {
                    style: "currency",
                    currency: capabilities.currency.toUpperCase()
                  })}`
                : "Unavailable"}
          </button>
        </div>
      </div>
      <div className={`validation-card ${issues.some((issue) => issue.severity === "error") ? "has-errors" : ""}`}>
        <h3>Validation report</h3>
        {issues.length === 0 ? (
          <p>All current dossier invariants pass.</p>
        ) : (
          <ul>
            {issues.map((issue) => (
              <li key={issue.id}>
                <strong>{issue.severity}:</strong> {issue.message}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function Details(props: DetailsProps) {
  const {
    activeTab,
    dossier,
    selectedFactionId,
    onSelectFaction,
    onEdit,
    onSelectRoom
  } = props;
  if (activeTab === "overview") {
    return (
      <Overview
        dossier={dossier}
        selectedFactionId={selectedFactionId}
        onSelectFaction={onSelectFaction}
        onMoveFaction={(id, x, y) =>
          onEdit((draft) => {
            const faction = draft.factions.find((candidate) => candidate.id === id);
            if (faction) {
              faction.x = x;
              faction.y = y;
            }
          })
        }
      />
    );
  }
  if (activeTab === "history") return <HistoryEditor dossier={dossier} onEdit={onEdit} />;
  if (activeTab === "lair") {
    return <LairEditor dossier={dossier} onEdit={onEdit} onSelectRoom={onSelectRoom} />;
  }
  if (activeTab === "hoard") return <HoardEditor dossier={dossier} onEdit={onEdit} />;
  if (activeTab === "factions") {
    return (
      <FactionsEditor
        dossier={dossier}
        selectedFactionId={selectedFactionId}
        onSelectFaction={onSelectFaction}
        onEdit={onEdit}
      />
    );
  }
  if (activeTab === "encounters") return <EncounterEditor dossier={dossier} onEdit={onEdit} />;
  if (activeTab === "hooks") return <HooksEditor dossier={dossier} onEdit={onEdit} />;
  if (activeTab === "rules") return <RulesEditor dossier={dossier} onEdit={onEdit} />;
  return <ExportView {...props} />;
}
